import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Copy, Trash2, X } from 'lucide-react'
import type { Scope } from '@rapid-response/shared'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { PermissionChecklist } from '~/components/admin/PermissionChecklist'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { useCurrentUser } from '~/lib/current-user'
import {
  createApplicationApiTokenRpc,
  listApplicationApiTokensRpc,
  revokeApplicationApiTokenRpc,
} from '~/server/app-api-keys'
import { seo } from '~/utils/seo'

export const Route = createFileRoute('/admin/api-keys')({
  head: () => ({ meta: seo({ title: 'Admin — API Keys' }) }),
  component: AdminApiKeysPage,
})

interface AppTokenRow {
  id: string
  name: string
  prefix: string
  scopes: Scope[]
  createdAt: string
  createdByUserId: string | null
  lastUsedAt: string | null
  expiresAt: string | null
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  } catch {
    toast.error('Failed to copy — select and copy manually')
  }
}

function AdminApiKeysPage() {
  const { user } = useCurrentUser()
  const [tokens, setTokens] = useState<AppTokenRow[] | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newScopes, setNewScopes] = useState<Set<Scope>>(new Set())
  const [newExpiresAt, setNewExpiresAt] = useState('')
  const [mintedPlaintext, setMintedPlaintext] = useState<string | null>(null)
  const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  async function load() {
    if (!user) return
    try {
      const rows = await listApplicationApiTokensRpc()
      setTokens(rows)
    } catch (e) {
      toast.error((e as Error).message)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  function resetCreateForm() {
    setNewName('')
    setNewScopes(new Set())
    setNewExpiresAt('')
  }

  async function handleCreate() {
    if (!user) return
    const name = newName.trim()
    if (!name) return
    if (newScopes.size === 0) {
      toast.error('Select at least one scope')
      return
    }
    setCreating(true)
    try {
      const result = await createApplicationApiTokenRpc({
        data: {
          name,
          scopes: Array.from(newScopes),
          expiresAt: newExpiresAt ? new Date(newExpiresAt).toISOString() : null,
        },
      })
      setMintedPlaintext(result.plaintext)
      resetCreateForm()
      await load()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setCreating(false)
    }
  }

  async function handleRevoke(tokenId: string) {
    if (!user) return
    setPendingRevokeId(tokenId)
    try {
      await revokeApplicationApiTokenRpc({
        data: { tokenId },
      })
      await load()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setPendingRevokeId(null)
    }
  }


  return (
    <div className="space-y-4 rounded-md border bg-card p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Application API Keys</h2>
          <p className="text-sm text-muted-foreground">
            Admin-managed credentials for automation and service-to-service calls.
            Unlike personal access tokens, these are not tied to a user — scopes
            set at mint time are the sole authorization signal.
          </p>
        </div>
        <Button
          onClick={() => {
            setMintedPlaintext(null)
            resetCreateForm()
            setCreateOpen(true)
          }}
        >
          Create key
        </Button>
      </div>

      {tokens === null && (
        <div className="text-muted-foreground text-sm">Loading keys...</div>
      )}
      {tokens !== null && tokens.length === 0 && (
        <p className="text-sm text-muted-foreground italic">No active keys.</p>
      )}
      {tokens !== null && tokens.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="py-2 font-medium">Name</th>
              <th className="py-2 font-medium">Prefix</th>
              <th className="py-2 font-medium">Scopes</th>
              <th className="py-2 font-medium">Created</th>
              <th className="py-2 font-medium">Expires</th>
              <th className="py-2 font-medium">Last used</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id} className="border-t align-top">
                <td className="py-2">{t.name}</td>
                <td className="py-2 font-mono text-xs">{t.prefix}…</td>
                <td className="py-2">
                  <div className="flex flex-wrap gap-1">
                    {t.scopes.map((s) => (
                      <Badge key={s} variant="secondary" className="font-mono text-xs">
                        {s}
                      </Badge>
                    ))}
                  </div>
                </td>
                <td className="py-2 text-muted-foreground">
                  {new Date(t.createdAt).toLocaleDateString()}
                </td>
                <td className="py-2 text-muted-foreground">
                  {t.expiresAt ? new Date(t.expiresAt).toLocaleDateString() : '—'}
                </td>
                <td className="py-2 text-muted-foreground">
                  {t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleDateString() : '—'}
                </td>
                <td className="py-2 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRevoke(t.id)}
                    disabled={pendingRevokeId === t.id}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {mintedPlaintext ? 'Copy your new key' : 'Create a new application API key'}
            </DialogTitle>
          </DialogHeader>

          {mintedPlaintext ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                This key will only be shown once. Store it somewhere safe — you
                won't be able to see it again.
              </p>
              <div className="flex items-center gap-2 rounded-md border bg-muted p-3">
                <code className="flex-1 break-all font-mono text-xs">
                  {mintedPlaintext}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(mintedPlaintext)}
                >
                  <Copy className="size-3.5" />
                </Button>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => {
                    setMintedPlaintext(null)
                    setCreateOpen(false)
                  }}
                >
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="keyName">Name</Label>
                <Input
                  id="keyName"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="ci-deployments"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  A short label to remember what this key is used for.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Scopes</Label>
                <PermissionChecklist value={newScopes} onChange={setNewScopes} />
              </div>

              <div className="space-y-1">
                <Label htmlFor="keyExpires">Expires (optional)</Label>
                <Input
                  id="keyExpires"
                  type="date"
                  value={newExpiresAt}
                  onChange={(e) => setNewExpiresAt(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank for a non-expiring key. The key stops working at
                  00:00 UTC on the chosen date.
                </p>
              </div>

              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => setCreateOpen(false)}
                  disabled={creating}
                >
                  <X className="size-3.5" />
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={!newName.trim() || newScopes.size === 0 || creating}
                >
                  {creating ? 'Creating...' : 'Create'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
